import React from 'react';

const steps = [
  {
    key: '01',
    title: 'Write',
    copy: 'Work in the actual LaTeX manuscript. Ask for a section, literature, a citation, a formula, or a diagram, then see the compiled paper and the change that was made.',
    visual: (
      <figure className="min-h-36" aria-label="A completed manuscript passage with a resolved citation">
        <div className="flex items-center justify-between border-b border-border/70 pb-3 text-xs text-muted-foreground">
          <span>Related work</span>
          <span className="font-medium text-primary">Citation resolved</span>
        </div>
        <blockquote className="mt-4 text-sm leading-6 text-foreground/80">
          Attention-based architectures replace recurrence with direct dependencies between sequence positions <span className="text-primary">[Vaswani et al., 2017]</span>.
        </blockquote>
        <figcaption className="mt-4 text-xs text-muted-foreground">Source added to the bibliography and compiled in the manuscript.</figcaption>
      </figure>
    ),
  },
  {
    key: '02',
    title: 'Check',
    copy: 'Read the draft from another angle. Request a structured AI review or invite a human reviewer to challenge the methodology, clarity, evidence, and contribution.',
    visual: (
      <figure className="min-h-36" aria-label="A specific methodology review finding">
        <div className="flex items-center justify-between border-b border-border/70 pb-3 text-xs">
          <span className="font-medium text-foreground">Methodology</span>
          <span className="text-muted-foreground">Major point</span>
        </div>
        <blockquote className="mt-4 border-l-2 border-primary/40 pl-4 text-sm leading-6 text-muted-foreground">
          The evaluation needs a clearer baseline and an explicit account of the sampling limits.
        </blockquote>
        <figcaption className="mt-4 text-xs text-muted-foreground">Linked to the evaluation section for revision.</figcaption>
      </figure>
    ),
  },
  {
    key: '03',
    title: 'Improve',
    copy: 'Revise against that feedback, compare versions, and keep the draft private. If publishing on PaperVerse serves the work, make that choice when it is ready.',
    visual: (
      <figure className="min-h-36" aria-label="A completed manuscript revision and successful compile">
        <div className="flex items-center justify-between border-b border-border/70 pb-3 text-xs">
          <span className="font-medium text-foreground">Version 4</span>
          <span className="font-medium text-primary">Compiled successfully</span>
        </div>
        <div className="mt-4 space-y-2 text-sm leading-6">
          <p className="text-muted-foreground line-through decoration-muted-foreground/60">We evaluated the method on several datasets.</p>
          <p className="border-l-2 border-primary/40 pl-4 text-foreground/80">We evaluated the method against three stated baselines across four public datasets.</p>
        </div>
        <figcaption className="mt-4 text-xs text-muted-foreground">Revision preserved as a new private version.</figcaption>
      </figure>
    ),
  },
];

const WorkflowSection = () => (
  <section className="px-5 py-16 md:px-8 md:py-24" aria-labelledby="workflow-title">
    <div className="mx-auto max-w-6xl">
      <div className="mb-12 max-w-2xl md:mb-16">
        <p className="mb-3 text-xs font-semibold uppercase text-primary">One connected process</p>
        <h2 id="workflow-title" className="text-3xl font-medium md:text-4xl">Write. Check. Improve.</h2>
      </div>

      <div className="relative ml-3 border-l border-border md:ml-5">
        {steps.map((step, index) => (
          <article
            key={step.title}
            className={`relative grid items-start gap-8 pb-14 pl-8 last:pb-0 md:grid-cols-[minmax(0,0.9fr)_minmax(22rem,1.1fr)] md:gap-16 md:pl-14 ${index === 0 ? 'md:pb-20' : 'md:pb-16'}`}
          >
            <span className="absolute -left-3 top-0 flex h-6 w-6 items-center justify-center rounded-full border border-primary/40 bg-background text-[10px] font-semibold text-primary">
              {step.key}
            </span>
            <div>
              <h3 className="mb-4 text-2xl font-medium">{step.title}</h3>
              <p className="max-w-xl text-base leading-8 text-muted-foreground md:text-lg">{step.copy}</p>
            </div>
            <div className="self-start border-y border-border/70 bg-muted/20 px-5 py-5 md:px-6 md:py-6">
              {step.visual}
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default WorkflowSection;